import { apiClient } from '../../services/api';
import { createClientRequestId } from '../../utils/clientRequestId';

const HEADERS = { 'X-Portal': 'business' };
const BASE = '/api/business/delivery';

function unwrap(response) {
  return response?.data?.data ?? response?.data ?? response;
}

/**
 * Writing off a parcel that is never coming back.
 *
 * D44: an exact asset confirmed missing, stolen, destroyed or otherwise
 * unrecoverable needs an authorized management-level disposition rather than a
 * fabricated return, and a Run may complete only once every physical asset has
 * either transferred or received one. Until this existed the command had no
 * caller at all, so a genuinely lost parcel left its Run unable to finish.
 *
 * The request deliberately carries no receiver and no rider: the server reads
 * the former Rider from locked custody and rejects either field outright, so
 * nobody can close the wrong person's accountability.
 */
export const businessAssetLossService = Object.freeze({
  async list(businessId) {
    const response = await apiClient.get(`${BASE}/asset-losses`, {
      headers: HEADERS,
      params: { businessId },
    });
    return unwrap(response);
  },

  async recordLoss(businessId, parcel, {
    reasonCode, justification, riderStatement, idempotencyKey,
  } = {}) {
    const response = await apiClient.post(`${BASE}/shipments/${parcel.shipmentId}/asset-loss`, {
      businessId,
      assetKind: 'PARCEL',
      reasonCode,
      justification,
      ...(riderStatement ? { riderStatement } : {}),
      expectedAssignmentVersion: parcel.assignmentVersion,
      expectedShipmentVersion: parcel.shipmentVersion,
    }, {
      headers: {
        ...HEADERS,
        'Idempotency-Key': idempotencyKey || createClientRequestId('asset_loss'),
      },
    });
    // 201 recorded it, 200 replayed an earlier identical request. The caller
    // needs to tell those apart so a retry does not report a second write-off.
    return { ...unwrap(response), replayed: response?.status === 200 };
  },
});
